import { isDefined } from "@clipboard-health/util-ts";
import { GlobalStyles, type SxProps, type Theme } from "@mui/material";
import { getLocationFromPlace, getMapApiWithPlaces } from "@src/appV2/lib/GoogleMaps/googleMaps";
import { TextInput } from "@src/appV2/redesign/components/TextInput";
import { useEffect, useRef } from "react";

export interface SelectedAddress {
  formattedAddress: string;
  latitude: number;
  longitude: number;
}

interface MobileAddressAutocompleteFieldProps {
  /** The current text shown in the field (the formatted address once selected). */
  value: string;
  /** Fired when the user picks a real address suggestion (has coordinates). */
  onSelect: (address: SelectedAddress) => void;
  /** Fired on free typing; the parent should treat this as "no valid address yet". */
  onInputChange: (value: string) => void;
  placeholder?: string;
  sx?: SxProps<Theme>;
}

/**
 * Address field backed by the Google Places Autocomplete. Unlike a plain text
 * input, it surfaces a real, geocoded address (formatted address + lat/long) via
 * `onSelect` so callers can require an actual address rather than free text.
 *
 * The wizard uses plain component state (not react-hook-form), so this is a thin
 * value/onChange wrapper around the Places API rather than `LocationAutoCompleteField`.
 */
export function MobileAddressAutocompleteField({
  value,
  onSelect,
  onInputChange,
  placeholder,
  sx,
}: MobileAddressAutocompleteFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  // Keep the latest onSelect so the Places listener always calls the current one.
  const onSelectRef = useRef(onSelect);
  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    let isActive = true;

    // The Places dropdown (.pac-container) is portaled to <body>, outside this
    // component, so the input's data-dd-privacy mask doesn't cover it. Tag it as
    // it appears so the suggested patient addresses (PHI) stay masked in Datadog.
    const dropdownMaskObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.classList.contains("pac-container")) {
            node.setAttribute("data-dd-privacy", "mask");
          }
        });
      });
    });
    dropdownMaskObserver.observe(document.body, { childList: true });

    async function initAutocomplete() {
      if (!isDefined(inputRef.current)) {
        return;
      }

      const mapApi = await getMapApiWithPlaces();
      if (!isActive || !isDefined(inputRef.current) || !isDefined(mapApi)) {
        return;
      }

      const autocomplete = new mapApi.places.Autocomplete(inputRef.current, {
        fields: ["formatted_address", "geometry"],
        types: ["address"],
      });
      autocompleteRef.current = autocomplete;

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();
        // Require real coordinates: getLocationFromPlace falls back to LA center
        // when geometry.location is missing, which would create a patient at a
        // bogus location, so bail unless we actually have a location.
        if (!isDefined(place.geometry?.location)) {
          return;
        }

        const { lat, lng } = getLocationFromPlace(place);
        onSelectRef.current({
          formattedAddress: place.formatted_address ?? "",
          latitude: lat,
          longitude: lng,
        });
      });
    }

    void initAutocomplete();

    return () => {
      isActive = false;
      dropdownMaskObserver.disconnect();
      if (isDefined(autocompleteRef.current)) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }

      document.querySelectorAll(".pac-container").forEach((container) => {
        container.remove();
      });
    };
  }, []);

  return (
    <>
      {/* The Places dropdown is portaled to <body>; lift it above the dialog. */}
      <GlobalStyles styles={{ ".pac-container": { zIndex: 2001 } }} />
      <TextInput
        fullWidth
        inputRef={inputRef}
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onInputChange(event.target.value);
        }}
        sx={sx}
        inputProps={{ "data-dd-privacy": "mask" }}
      />
    </>
  );
}
